import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { wwColors } from "../theme";
import ShipHelm from "./ShipHelm";

const LINE_CORE = 6;
const LINE_MID = 11;
const LINE_GLOW = 20;

/** Android shuffle: spiral implode → reorder → spiral explode (~1.55s). */
const SHUFFLE_IMPLODE_MS = 720;
const SHUFFLE_EXPLODE_MS = 830;
const SPIRAL_TURNS = 1;
/** Legacy straight-slide fallback when callers reorder tiles without shuffleSignal. */
const SHUFFLE_SLIDE_MS = 480;

const noop = () => {};

function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;
}

function easeOutCubic(t) {
    return 1 - (1 - t) ** 3;
}

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Animate `from` → `to` over `durationMs` via rAF.
 * @returns {() => void} cancel
 */
function animateProgress(from, to, durationMs, easing, onFrame, onDone) {
    const start = performance.now();
    let raf = 0;
    let cancelled = false;
    const tick = (now) => {
        if (cancelled) return;
        const t = Math.min(1, (now - start) / durationMs);
        onFrame(from + (to - from) * easing(t));
        if (t < 1) {
            raf = requestAnimationFrame(tick);
        } else {
            onDone?.();
        }
    };
    raf = requestAnimationFrame(tick);
    return () => {
        cancelled = true;
        cancelAnimationFrame(raf);
    };
}

/** Spiral offset matching Android WheelLetter (progress 0 = ring, 1 = center). */
function spiralStyle(x, y, centerX, centerY, progress) {
    const p = Math.max(0, Math.min(1, progress));
    const dx = x - centerX;
    const dy = y - centerY;
    const baseRadius = Math.hypot(dx, dy);
    const baseAngle = Math.atan2(dy, dx);
    const angle = baseAngle + p * SPIRAL_TURNS * Math.PI * 2;
    const r = baseRadius * (1 - p);
    const currX = centerX + r * Math.cos(angle);
    const currY = centerY + r * Math.sin(angle);
    const ringScale = Math.max(0, 1 - p);
    return {
        transform: `translate(${currX - x}px, ${currY - y}px) rotate(${p * SPIRAL_TURNS * 360}deg) scale(${ringScale})`,
        opacity: ringScale,
    };
}

function buildNodes(count, wheelPx, nodePx, edgeInset = 2) {
    const ringRadius = wheelPx / 2 - nodePx / 2 - edgeInset;
    const n = count;
    if (n === 0) return [];
    return Array.from({ length: n }, (_, i) => {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        return {
            index: i,
            x: wheelPx / 2 + ringRadius * Math.cos(angle),
            y: wheelPx / 2 + ringRadius * Math.sin(angle),
        };
    });
}

function nodePositionsForCount(count, wheelPx, nodePx, edgeInset = 2) {
    return buildNodes(count, wheelPx, nodePx, edgeInset);
}

function polylineLength(points) {
    let length = 0;
    for (let i = 1; i < points.length; i += 1) {
        length += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    }
    return length;
}

/** Keep the path from the first letter up to `remaining` pixels. The tip walks backward as remaining shrinks. */
function trimPath(points, remaining) {
    if (!points.length) return [];
    if (remaining <= 0) return [points[0]];
    const out = [points[0]];
    let left = remaining;
    for (let i = 1; i < points.length; i += 1) {
        const dx = points[i].x - points[i - 1].x;
        const dy = points[i].y - points[i - 1].y;
        const seg = Math.hypot(dx, dy);
        if (left >= seg - 0.01) {
            out.push(points[i]);
            left -= seg;
            continue;
        }
        const u = seg === 0 ? 0 : left / seg;
        out.push({
            x: points[i - 1].x + dx * u,
            y: points[i - 1].y + dy * u,
            index: null,
        });
        break;
    }
    return out;
}

function nodeAtPoint(nodes, clientX, clientY, wheelRect, nodePx) {
    const x = clientX - wheelRect.left;
    const y = clientY - wheelRect.top;
    const hit = nodePx * 0.75;
    let best = null;
    let bestDist = hit;
    for (const node of nodes) {
        const d = Math.hypot(node.x - x, node.y - y);
        if (d < bestDist) {
            bestDist = d;
            best = node;
        }
    }
    return best;
}

/** Android WheelSegment — soft glow → mid → core (amber on teal). */
function WheelSegment({ x1, y1, x2, y2, active = false, line, lineDark, lineSoft }) {
    const glowOpacity = active ? 0.5 : 0.35;
    return (
        <>
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={lineSoft || line}
                strokeWidth={LINE_GLOW}
                strokeLinecap="round"
                opacity={glowOpacity}
            />
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={line}
                strokeWidth={LINE_MID}
                strokeLinecap="round"
                opacity={0.85}
            />
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={lineDark}
                strokeWidth={LINE_CORE}
                strokeLinecap="round"
            />
        </>
    );
}

/** Live line from last selected node to finger (Android DraggingLine). */
function DraggingLine({ x1, y1, x2, y2, line, lineDark, lineSoft }) {
    return (
        <>
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={lineSoft || line}
                strokeWidth={LINE_GLOW}
                strokeLinecap="round"
                opacity={0.45}
            />
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={line}
                strokeWidth={LINE_MID}
                strokeLinecap="round"
                opacity={0.85}
            />
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={lineDark}
                strokeWidth={LINE_CORE}
                strokeLinecap="round"
            />
        </>
    );
}

export default function LetterWheel({
    tiles = [],
    selectedIndices = [],
    onSelectionChange = noop,
    onDragEnd,
    fill,
    colors = wwColors,
    /** Optional wooden helm (or other) image under the letter ring; defaults to ShipHelm SVG. */
    helmImageSrc = null,
    /** `helm` (default) | `image` | `gradient` — gradient is a soft filled circle; letters sit on the rim. */
    backdrop = null,
    /** Pull letters toward the outer edge (px inset from rim). Default 2. */
    edgeInset = 2,
    /** Android-style shuffle: called at implode center to reorder tiles. */
    onShuffle = null,
    /** Bump to trigger spiral implode → onShuffle → explode. */
    shuffleSignal = 0,
}) {
    const backdropMode = backdrop || (helmImageSrc ? "image" : "helm");
    const containerRef = useRef(null);
    const wheelRef = useRef(null);
    const draggingRef = useRef(false);
    const selectedIndicesRef = useRef(selectedIndices);
    const fingerPosRef = useRef(null);
    const retractCancelRef = useRef(null);
    const nodesRef = useRef([]);
    const nodePxRef = useRef(52);
    const tileElsRef = useRef({});
    const slotByIdRef = useRef(new Map());
    const onShuffleRef = useRef(onShuffle);
    const spiralShuffleRef = useRef(false);
    const [wheelPx, setWheelPx] = useState(fill ? 200 : 300);
    const [shuffleGhosts, setShuffleGhosts] = useState([]);
    const [isShuffling, setIsShuffling] = useState(false);
    const [shuffleProgress, setShuffleProgress] = useState(0);
    const [fingerPos, setFingerPos] = useState(null);
    const [retract, setRetract] = useState(null);

    selectedIndicesRef.current = selectedIndices;
    fingerPosRef.current = fingerPos;

    onShuffleRef.current = onShuffle;

    useEffect(() => () => {
        retractCancelRef.current?.();
    }, []);

    const nodePx = Math.max(fill ? 22 : 36, Math.round(wheelPx * 0.17));
    const nodes = useMemo(
        () => nodePositionsForCount(tiles.length, wheelPx, nodePx, edgeInset),
        [tiles.length, wheelPx, nodePx, edgeInset]
    );
    const wheelCenter = wheelPx / 2;

    nodesRef.current = nodes;
    nodePxRef.current = nodePx;

    // Android spiral shuffle driven by shuffleSignal.
    useEffect(() => {
        if (!shuffleSignal) return undefined;
        if (spiralShuffleRef.current) return undefined;
        if (!onShuffleRef.current) return undefined;

        spiralShuffleRef.current = true;
        setIsShuffling(true);
        onSelectionChange([]);
        fingerPosRef.current = null;
        setFingerPos(null);
        retractCancelRef.current?.();
        retractCancelRef.current = null;
        setRetract(null);
        draggingRef.current = false;

        let cancelExplodeAnim = null;
        let explodeRaf = 0;
        const cancelImplode = animateProgress(
            0,
            1,
            SHUFFLE_IMPLODE_MS,
            easeInOutCubic,
            setShuffleProgress,
            () => {
                onShuffleRef.current?.();
                // Match Android: one frame so new seats commit before explode.
                explodeRaf = requestAnimationFrame(() => {
                    cancelExplodeAnim = animateProgress(
                        1,
                        0,
                        SHUFFLE_EXPLODE_MS,
                        easeOutCubic,
                        setShuffleProgress,
                        () => {
                            spiralShuffleRef.current = false;
                            setIsShuffling(false);
                            setShuffleProgress(0);
                        },
                    );
                });
            },
        );

        return () => {
            cancelImplode();
            cancelAnimationFrame(explodeRaf);
            cancelExplodeAnim?.();
            spiralShuffleRef.current = false;
            setIsShuffling(false);
            setShuffleProgress(0);
        };
    }, [shuffleSignal, onSelectionChange]);

    useLayoutEffect(() => {
        if (!tiles.length) {
            slotByIdRef.current = new Map();
            return undefined;
        }

        const prevSlots = slotByIdRef.current;
        const currentIds = tiles.map((tile) => tile.id);
        const idsChanged = prevSlots.size > 0 && currentIds.some((id) => !prevSlots.has(id));

        if (idsChanged) {
            prevSlots.clear();
            tiles.forEach((tile, index) => prevSlots.set(tile.id, index));
            return undefined;
        }

        const isFirstLayout = prevSlots.size === 0;
        const orderChanged = !isFirstLayout && tiles.some((tile, index) => prevSlots.get(tile.id) !== index);

        if (isFirstLayout) {
            tiles.forEach((tile, index) => prevSlots.set(tile.id, index));
            return undefined;
        }

        if (!orderChanged) {
            tiles.forEach((tile, index) => prevSlots.set(tile.id, index));
            return undefined;
        }

        // Spiral shuffle owns motion — just sync slot map when tiles reorder mid-implode.
        if (spiralShuffleRef.current || onShuffleRef.current) {
            tiles.forEach((tile, index) => prevSlots.set(tile.id, index));
            return undefined;
        }

        const positions = nodePositionsForCount(tiles.length, wheelPx, nodePx, edgeInset);
        const startPosById = new Map();
        tiles.forEach((tile) => {
            const oldIndex = prevSlots.get(tile.id);
            if (oldIndex == null || !positions[oldIndex]) return;
            startPosById.set(tile.id, positions[oldIndex]);
        });

        setShuffleGhosts(
            tiles
                .map((tile) => {
                    const start = startPosById.get(tile.id);
                    if (!start) return null;
                    return { id: tile.id, letter: tile.letter, x: start.x, y: start.y };
                })
                .filter(Boolean)
        );
        setIsShuffling(true);

        tiles.forEach((tile, index) => {
            const start = startPosById.get(tile.id);
            const end = positions[index];
            const el = tileElsRef.current[tile.id];
            if (!el || !start || !end) return;
            const dx = start.x - end.x;
            const dy = start.y - end.y;
            if (dx === 0 && dy === 0) return;
            el.style.transition = "none";
            el.style.transform = `translate(${dx}px, ${dy}px)`;
        });

        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                tiles.forEach((tile, index) => {
                    const start = startPosById.get(tile.id);
                    const end = positions[index];
                    const el = tileElsRef.current[tile.id];
                    if (!el || !start || !end) return;
                    if (start.x === end.x && start.y === end.y) return;
                    el.style.transition = `transform ${SHUFFLE_SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
                    el.style.transform = "";
                });
                setShuffleGhosts((prev) => prev.map((ghost) => ({ ...ghost, fading: true })));
            });
        });

        const timer = window.setTimeout(() => {
            setShuffleGhosts([]);
            setIsShuffling(false);
            tiles.forEach((tile, index) => prevSlots.set(tile.id, index));
        }, SHUFFLE_SLIDE_MS + 60);

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            window.clearTimeout(timer);
        };
    }, [tiles, wheelPx, nodePx, edgeInset]);

    useLayoutEffect(() => {
        if (!fill || !containerRef.current) return undefined;
        const el = containerRef.current;
        const measure = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            const next = Math.max(fill ? 96 : 120, Math.floor(Math.min(w, h)));
            setWheelPx((prev) => (prev === next ? prev : next));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [fill]);

    const appendIndex = useCallback((index) => {
        onSelectionChange((prev) => {
            if (prev.length === 0) return [index];
            const last = prev[prev.length - 1];
            if (last === index) return prev;
            if (prev.length >= 2 && prev[prev.length - 2] === index) {
                return prev.slice(0, -1);
            }
            return [...prev, index];
        });
    }, [onSelectionChange]);

    const pointerToWheel = useCallback((clientX, clientY) => {
        const wheelEl = wheelRef.current;
        if (!wheelEl) return null;
        const rect = wheelEl.getBoundingClientRect();
        return {
            x: clientX - rect.left,
            y: clientY - rect.top,
        };
    }, []);

    const pickAtPoint = useCallback((clientX, clientY) => {
        const wheelEl = wheelRef.current;
        if (!wheelEl) return;
        const rect = wheelEl.getBoundingClientRect();
        const hit = nodeAtPoint(nodesRef.current, clientX, clientY, rect, nodePxRef.current);
        if (hit) appendIndex(hit.index);
    }, [appendIndex]);

    const cancelRetract = useCallback(() => {
        retractCancelRef.current?.();
        retractCancelRef.current = null;
        setRetract(null);
    }, []);

    const startRetract = useCallback((points) => {
        cancelRetract();
        const length = polylineLength(points);
        if (length < 1) return;
        const reduceMotion = prefersReducedMotion();
        if (reduceMotion) return;
        const duration = Math.min(720, Math.max(220, (points.length - 1) * 150));
        setRetract({ points, remaining: length });
        retractCancelRef.current = animateProgress(
            length,
            0,
            duration,
            easeInOutCubic,
            (remaining) => setRetract({ points, remaining }),
            () => {
                retractCancelRef.current = null;
                setRetract(null);
            },
        );
    }, [cancelRetract]);

    const finishDrag = useCallback(() => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        const indices = selectedIndicesRef.current || [];
        const nodesNow = nodesRef.current;
        const points = [];
        indices.forEach((index) => {
            const node = nodesNow[index];
            if (!node) return;
            points.push({ x: node.x, y: node.y, index });
        });
        const finger = fingerPosRef.current;
        const last = points[points.length - 1];
        if (finger && last && Math.hypot(finger.x - last.x, finger.y - last.y) > 10) {
            points.push({ x: finger.x, y: finger.y, index: null });
        }
        fingerPosRef.current = null;
        setFingerPos(null);
        if (points.length >= 2) startRetract(points);
        onDragEnd?.();
    }, [onDragEnd, startRetract]);

    const handlePointerDown = useCallback((e, index) => {
        if (isShuffling) return;
        e.preventDefault();
        e.stopPropagation();
        cancelRetract();
        draggingRef.current = true;
        onSelectionChange([index]);
        const pos = pointerToWheel(e.clientX, e.clientY);
        if (pos) {
            fingerPosRef.current = pos;
            setFingerPos(pos);
        }

        const onMove = (ev) => {
            if (!draggingRef.current) return;
            const nextPos = pointerToWheel(ev.clientX, ev.clientY);
            if (nextPos) {
                fingerPosRef.current = nextPos;
                setFingerPos(nextPos);
            }
            pickAtPoint(ev.clientX, ev.clientY);
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
            finishDrag();
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
    }, [onSelectionChange, pickAtPoint, finishDrag, isShuffling, pointerToWheel, cancelRetract]);

    const retractVisible = retract ? trimPath(retract.points, retract.remaining) : null;
    const retractIndices = retractVisible
        ? new Set(retractVisible.map((point) => point.index).filter((index) => index != null))
        : null;
    const retractActiveIndex = retractVisible
        ? [...retractVisible].reverse().find((point) => point.index != null)?.index
        : null;

    const renderLetterNode = (letter, index, tileId, options = {}) => {
        const { isGhost = false, ghostX, ghostY, ghostFading = false } = options;
        const node = isGhost ? { x: ghostX, y: ghostY } : nodes[index];
        if (!node) return null;

        const isSelected = !isGhost && (
            retractIndices
                ? retractIndices.has(index)
                : selectedIndices.includes(index)
        );
        const isActive = !isGhost && (
            retractIndices
                ? index === retractActiveIndex
                : selectedIndices[selectedIndices.length - 1] === index
        );
        const spiral = !isGhost && shuffleProgress > 0
            ? spiralStyle(node.x, node.y, wheelCenter, wheelCenter, shuffleProgress)
            : null;

        return (
            <Box
                key={isGhost ? `ghost-${tileId}` : tileId}
                ref={isGhost ? undefined : (el) => {
                    if (el) tileElsRef.current[tileId] = el;
                    else delete tileElsRef.current[tileId];
                }}
                data-word-wheel-letter={isGhost ? undefined : true}
                onPointerDown={isGhost ? undefined : (e) => handlePointerDown(e, index)}
                sx={{
                    position: "absolute",
                    left: node.x - nodePx / 2,
                    top: node.y - nodePx / 2,
                    width: nodePx,
                    height: nodePx,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: nodePx * 0.42,
                    fontWeight: 800,
                    color: isGhost
                        ? (colors.letterText || colors.text || "#ffffff")
                        : isSelected
                            ? (colors.letterSelectedText || colors.selectedText || "#0B3D2E")
                            : (colors.letterText || colors.text || "#ffffff"),
                    cursor: isGhost || isShuffling ? "default" : "pointer",
                    touchAction: "none",
                    pointerEvents: isGhost || isShuffling ? "none" : "auto",
                    opacity: isGhost
                        ? (ghostFading ? 0 : 0.72)
                        : (spiral ? spiral.opacity : 1),
                    transform: spiral ? spiral.transform : undefined,
                    transition: isGhost
                        ? `opacity ${SHUFFLE_SLIDE_MS}ms ease`
                        : spiral
                            ? "none"
                            : "box-shadow 0.15s ease, background-color 0.15s ease",
                    border: isGhost
                        ? `1px dashed ${colors.accentRing}`
                        : isSelected
                            ? `2.5px solid ${colors.selectedBorder || colors.accentDark || "#0B3D2E"}`
                            : `1px solid ${colors.border}`,
                    bgcolor: isGhost
                        ? colors.accentSoft
                        : isSelected
                            ? (colors.selectedSurface || "#ffffff")
                            : (colors.letterBg || colors.surface),
                    boxShadow: isGhost
                        ? "none"
                        : isActive
                            ? `0 0 0 3px ${colors.accentRing || "rgba(11, 61, 46, 0.35)"}, ${colors.shadowMd}`
                            : isSelected
                                ? (colors.shadowMd || colors.shadowSm)
                                : colors.shadowSm,
                    zIndex: isGhost ? 1 : 2,
                    willChange: spiral ? "transform, opacity" : undefined,
                }}
            >
                {letter}
            </Box>
        );
    };

    const lineStroke = colors.wheelLine || colors.accent || "#fcd34d";
    const lineStrokeDark = colors.wheelLineDark || colors.accentDark || "#f59e0b";
    const lineStrokeSoft = colors.wheelLineSoft || colors.wheelLineGlow || "rgba(254, 243, 199, 0.9)";
    const staticSegmentCount = retractVisible
        ? Math.max(0, retractVisible.length - 1)
        : Math.max(0, selectedIndices.length - 1);
    const dragFrom = retractVisible
        ? null
        : (selectedIndices.length >= 1 ? nodes[selectedIndices[selectedIndices.length - 1]] : null);
    const isDragging = Boolean(!retractVisible && fingerPos && dragFrom);
    const helmInset = nodePx / 2 + 6;

    return (
        <Box
            ref={containerRef}
            sx={{
                position: "relative",
                width: fill ? "100%" : wheelPx,
                height: fill ? "100%" : wheelPx,
                maxWidth: fill ? "100%" : wheelPx,
                maxHeight: fill ? "100%" : wheelPx,
                mx: "auto",
                touchAction: "none",
                userSelect: "none",
                cursor: isShuffling ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Box
                ref={wheelRef}
                sx={{
                    position: "relative",
                    width: wheelPx,
                    height: wheelPx,
                    flexShrink: 0,
                    touchAction: "none",
                }}
            >
                {backdropMode === "image" && helmImageSrc ? (
                    <Box
                        component="img"
                        src={helmImageSrc}
                        alt=""
                        draggable={false}
                        sx={{
                            position: "absolute",
                            left: helmInset * 0.15,
                            top: helmInset * 0.15,
                            width: wheelPx - helmInset * 0.3,
                            height: wheelPx - helmInset * 0.3,
                            objectFit: "contain",
                            pointerEvents: "none",
                            userSelect: "none",
                            opacity: 0.95,
                        }}
                    />
                ) : backdropMode === "gradient" ? (
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: Math.max(4, nodePx * 0.15),
                            borderRadius: "50%",
                            pointerEvents: "none",
                            background: `
                                radial-gradient(
                                    circle at 32% 28%,
                                    rgba(232, 255, 242, 0.95) 0%,
                                    rgba(127, 211, 168, 0.75) 28%,
                                    rgba(59, 166, 102, 0.88) 58%,
                                    rgba(30, 92, 58, 0.95) 100%
                                )
                            `,
                            boxShadow: `
                                inset 0 0 0 2px rgba(255, 255, 255, 0.35),
                                inset 0 8px 24px rgba(255, 255, 255, 0.18),
                                0 10px 28px rgba(40, 90, 60, 0.28)
                            `,
                        }}
                    />
                ) : (
                    <ShipHelm size={wheelPx} inset={helmInset} />
                )}

                <svg
                    width={wheelPx}
                    height={wheelPx}
                    style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}
                >
                    {Array.from({ length: staticSegmentCount }, (_, i) => {
                        const a = retractVisible ? retractVisible[i] : nodes[selectedIndices[i]];
                        const b = retractVisible ? retractVisible[i + 1] : nodes[selectedIndices[i + 1]];
                        if (!a || !b) return null;
                        const isActive = retractVisible
                            ? i === staticSegmentCount - 1
                            : isDragging && i === staticSegmentCount - 1 && selectedIndices.length > 1;
                        const key = retractVisible
                            ? `retract-${i}`
                            : `seg-${selectedIndices[i]}-${selectedIndices[i + 1]}`;
                        return (
                            <WheelSegment
                                key={key}
                                x1={a.x}
                                y1={a.y}
                                x2={b.x}
                                y2={b.y}
                                active={isActive}
                                line={lineStroke}
                                lineDark={lineStrokeDark}
                                lineSoft={lineStrokeSoft}
                            />
                        );
                    })}
                    {isDragging && dragFrom && fingerPos ? (
                        <DraggingLine
                            x1={dragFrom.x}
                            y1={dragFrom.y}
                            x2={fingerPos.x}
                            y2={fingerPos.y}
                            line={lineStroke}
                            lineDark={lineStrokeDark}
                            lineSoft={lineStrokeSoft}
                        />
                    ) : null}
                </svg>

                {shuffleGhosts.map((ghost) => renderLetterNode(
                    ghost.letter,
                    -1,
                    ghost.id,
                    { isGhost: true, ghostX: ghost.x, ghostY: ghost.y, ghostFading: ghost.fading }
                ))}

                {tiles.map((tile, index) => renderLetterNode(tile.letter, index, tile.id))}
            </Box>
        </Box>
    );
}

export function wheelLettersFromWords(targetWords) {
    const maxFreq = new Map();
    (targetWords || []).forEach((word) => {
        const freq = new Map();
        String(word || "")
            .toUpperCase()
            .split("")
            .forEach((ch) => {
                if (/[A-Z]/.test(ch)) {
                    freq.set(ch, (freq.get(ch) || 0) + 1);
                }
            });
        freq.forEach((count, ch) => {
            maxFreq.set(ch, Math.max(maxFreq.get(ch) || 0, count));
        });
    });
    const tiles = [];
    [...maxFreq.keys()].sort().forEach((ch) => {
        for (let i = 0; i < maxFreq.get(ch); i += 1) {
            tiles.push(ch);
        }
    });
    return tiles;
}

export function lettersForWheel(targetWords, lettersGrid, details) {
    const fromDetails = details?.characters;
    if (Array.isArray(fromDetails) && fromDetails.length > 0) {
        return fromDetails
            .map((cell) => (cell || "").trim().toUpperCase())
            .filter(Boolean);
    }
    if (targetWords?.length > 0) {
        return wheelLettersFromWords(targetWords);
    }
    const fromGrid = new Set();
    if (Array.isArray(lettersGrid)) {
        lettersGrid.forEach((row) => {
            if (!Array.isArray(row)) return;
            row.forEach((cell) => {
                const ch = (cell || "").trim().toUpperCase();
                if (ch) fromGrid.add(ch);
            });
        });
    }
    if (fromGrid.size > 0) {
        return [...fromGrid].sort();
    }
    return [];
}

/** Build stable tile ids for shuffle animation (one id per letter instance). */
export function buildWheelTiles(letters, seed = "wheel") {
    const list = Array.isArray(letters) ? letters : [];
    return list.map((letter, index) => ({
        id: `${seed}-tile-${index}`,
        letter,
    }));
}

/** Randomize tile order on the wheel (same letters, new positions). */
export function shuffleWheelTiles(tiles) {
    const next = [...tiles];
    for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
    }
    if (next.length > 1 && next.every((tile, index) => tile.id === tiles[index].id)) {
        [next[0], next[1]] = [next[1], next[0]];
    }
    return next;
}

/** @deprecated Use shuffleWheelTiles with buildWheelTiles instead. */
export function shuffleWheelLetters(letters) {
    return shuffleWheelTiles(buildWheelTiles(letters)).map((tile) => tile.letter);
}
